// src/features/system/developer/components/simulations/simTabGates.ts
// ────────────────────────────────────────────────────────────────────────────
// Simulator Tab Gates — declarative, composable visibility predicates
//
// Each tab has a `visible(ctx)` predicate. The active tab list is computed by
// evaluating every gate against the current render context (auth state, role
// flags, render mode). Gates are pure functions → trivially testable.
//
// Adding a tab:  push a new entry with a predicate.
// Restricting:   tighten the predicate (no changes to SimulatorPanel needed).
// Public mode:   pass `mode: 'public'` in the context; only tabs whose gate
//                returns true for the public ctx will render.
// ────────────────────────────────────────────────────────────────────────────

export const ALL_SIM_TABS = [
  'Electron',
  'String',
  'Music',
  'KDABRA',
  'MidiDAW',
  'OledSim',
  'TableOps',
  'RetailOps',
  'MercadoPago',
  'StepRender',
  'TrafficSim',
  'DaemonOps',
] as const

export type SimTab = (typeof ALL_SIM_TABS)[number]

export type SimGateMode = 'public' | 'full'

export interface SimGateContext {
  /** Whether the caller holds an authenticated IAM session. */
  isAuthenticated: boolean
  /** Superadmin wildcard permission. */
  isSuperadmin: boolean
  /** Render mode. Public mode = anonymous visitor landing. */
  mode: SimGateMode
}

export interface SimTabGate {
  key: SimTab
  /** Returns true when the tab should be listed for the given context. */
  visible: (ctx: SimGateContext) => boolean
}

/**
 * Public-visitor whitelist — the ONLY tabs surfaced when `mode === 'public'`.
 *
 * All entries here MUST be self-contained client-side simulators with no
 * authenticated BFF calls (no fetchData to /bff/* gated routes, no RBAC
 * lookups).  Verified: Electron, String, KDABRA, Music, MidiDAW are all
 * pure-WebAudio / pure-WebGL client simulations.
 */
const PUBLIC_WHITELIST = new Set<SimTab>([
  'Electron',
  'String',
  'KDABRA',
  'Music',
  'MidiDAW',
])

/** Helper: tab is visible in public mode (no auth required). */
const publicOk = (key: SimTab) => (ctx: SimGateContext) =>
  ctx.mode === 'public' ? PUBLIC_WHITELIST.has(key) : true

/** Helper: tab is authenticated-only (hidden in public mode). */
const authOnly = (ctx: SimGateContext) => ctx.mode !== 'public' && ctx.isAuthenticated

/** Helper: tab is superadmin-only and auth required. */
const superadminOnly = (ctx: SimGateContext) =>
  ctx.mode !== 'public' && ctx.isSuperadmin

/**
 * Canonical gate registry. Order here controls tab-strip order.
 * Edit this table — SimulatorPanel consumes it verbatim.
 */
export const SIM_TAB_GATES: readonly SimTabGate[] = [
  { key: 'Electron',    visible: publicOk('Electron') },
  { key: 'String',      visible: publicOk('String') },
  { key: 'KDABRA',      visible: publicOk('KDABRA') },
  { key: 'Music',       visible: publicOk('Music') },
  { key: 'MidiDAW',     visible: publicOk('MidiDAW') },
  { key: 'OledSim',     visible: authOnly },
  { key: 'TableOps',    visible: authOnly },
  { key: 'RetailOps',   visible: authOnly },
  { key: 'MercadoPago', visible: authOnly },
  { key: 'StepRender',  visible: authOnly },
  { key: 'TrafficSim',  visible: superadminOnly },
  { key: 'DaemonOps',   visible: superadminOnly },
]

/** Compute the visible tab list for a context. Preserves gate order. */
export function resolveSimTabs(ctx: SimGateContext): SimTab[] {
  return SIM_TAB_GATES.filter(g => g.visible(ctx)).map(g => g.key)
}
